import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Video, Car } from 'lucide-react';
import CTA from '../../components/CTA';

const ArmaghHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Armagh | Online Sessions from Belfast - No Travel Required</title>
        <meta
          name="description"
          content="Professional hypnotherapy serving Armagh - online sessions recommended (40+ miles from Belfast). Student anxiety, religious community stress, rural isolation support. CNHC registered."
        />
        <meta name="keywords" content="hypnotherapy Armagh, online hypnotherapy Armagh, hypnotherapist Armagh, student anxiety Southern Regional College, religious community stress, rural mental health Drumbreda Callan Bridge" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/armagh-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Armagh | Online Sessions - No Travel Required" />
        <meta property="og:description" content="Professional hypnotherapy serving Armagh online. Student anxiety, religious community stress, rural mental health support without 40-mile travel." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Armagh",
            "description": "CNHC registered hypnotherapy serving Armagh online and from Belfast clinic. Specializing in student mental health, religious community stress, anxiety management, rural isolation support, and confidential therapy outside traditional community networks.",
            "url": "https://www.sharonclare-hypnotherapy.com/armagh-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Armagh",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Armagh"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Online Hypnotherapy",
              "Student Mental Health Support",
              "Religious Community Stress",
              "Anxiety Treatment",
              "Rural Isolation Support",
              "Smoking Cessation"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy Serving Armagh
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Online sessions recommended - same quality therapy without the 40-mile travel
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block border-2 border-accent-500">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <Video className="w-8 h-8 text-accent-600" />
                <p className="text-xl font-semibold text-gray-900">
                  Online Sessions Available - No Travel Required
                </p>
              </div>
              <p className="text-gray-700 mb-2">
                Professional video therapy from your Armagh home - same effectiveness, zero commute
              </p>
              <div className="flex items-center justify-center space-x-2 text-gray-600 text-sm pt-2 border-t border-gray-200">
                <Car className="w-5 h-5" />
                <span>Belfast clinic option: 40+ miles via M1/A3 • 50-60 min drive</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Armagh's Ecclesiastical City Community
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I'm delighted to serve Armagh's unique community with professional hypnotherapy - and I want to be upfront about accessibility from the start. While I'm based at Belfast Wellness Clinic, 228 Ormeau Road, Belfast in Belfast, Armagh is 40+ miles away via the M1 and A3 route, taking 50-60 minutes by car. That's a significant journey for regular weekly therapy sessions, and I recognize that the round trip would add over 2 hours to each appointment - time that's precious when you're already busy.
              </p>
              <p>
                <strong>This is why I recommend online video sessions as the primary option for Armagh clients.</strong> Online hypnotherapy is equally effective as in-person sessions - hypnosis works through voice, guidance, and your own subconscious processes, not physical presence. You receive the same professional support, the same CNHC-registered expertise, and the same evidence-based techniques, but without losing half your day to travel. For students at Southern Regional College, working professionals, parents juggling family commitments, or anyone in rural County Armagh, online sessions provide flexibility that makes consistent therapy actually achievable.
              </p>
              <p>
                Armagh, the ecclesiastical capital of Ireland and a city of approximately 15,000 people, has a distinctive character shaped by its two cathedrals, its historic architecture, and its role as a county town serving rural communities. Whether you're a student managing exam stress and adjustment challenges, someone navigating religious community expectations, a rural resident experiencing isolation, or anyone in Armagh dealing with anxiety, poor sleep, chronic pain, unwanted habits, or other concerns, hypnotherapy offers confidential, professional support that respects your privacy and fits your schedule - no 80-mile round trip required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Online Sessions Work for Armagh */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Why Online Sessions Are Ideal for Armagh Clients
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Clock className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Save 2+ Hours Per Session
                    </h3>
                    <p className="text-gray-700">
                      The 40-mile journey from Armagh to Belfast takes 50-60 minutes each way - that's over 2 hours of travel for a 60-90 minute session. Online therapy eliminates commute time entirely, making weekly sessions realistic for busy schedules.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Same Effectiveness as In-Person
                    </h3>
                    <p className="text-gray-700">
                      Research shows online hypnotherapy is equally effective as face-to-face sessions. Hypnosis works through verbal guidance and your subconscious processes - physical proximity isn't required. You get professional CNHC-registered therapy without compromise.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <MapPin className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Privacy in a Close-Knit Community
                    </h3>
                    <p className="text-gray-700">
                      In Armagh's tight-knit environment where "everyone knows everyone," online sessions provide complete confidentiality. No one needs to know you're seeking support - therapy from your own private space without the visibility of traveling to a clinic.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Video className="w-8 h-8 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                      Flexible Scheduling for Students & Workers
                    </h3>
                    <p className="text-gray-700">
                      Southern Regional College students can fit sessions between lectures without travel. Working professionals can schedule appointments during lunch or after work without the 2-hour commute. Parents can book sessions during school hours without arranging childcare for travel time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-accent-50 rounded-xl p-6 border-l-4 border-accent-600">
              <p className="text-gray-800">
                <strong>Distance honesty builds trust:</strong> I could tell you "Belfast is easily accessible from Armagh" but that wouldn't be honest. 40+ miles is significant. Online sessions aren't a compromise or second-best option - they're the smart choice for consistent, effective therapy without the burden of regular long-distance travel. Many Armagh clients prefer online even when they could travel, simply because it's more sustainable long-term.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Armagh Community */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Services Tailored to Armagh's Ecclesiastical City Community
            </h2>
            <p className="text-lg text-gray-700 mb-12">
              Living in Armagh brings particular stressors - the pressures of religious community expectations, the challenges of student life in a smaller city, the isolation of rural County Armagh, and limited access to local mental health services. Hypnotherapy addresses these unique circumstances with confidential, professional support delivered online for maximum accessibility.
            </p>

            <div className="space-y-10">
              {/* Student Mental Health */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Student Mental Health Support (Southern Regional College)
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Southern Regional College's Armagh campus brings a student population to this historic city, and with that comes the universal challenges of higher education: exam anxiety, assignment stress, career uncertainty, financial pressures, social anxiety, and the adjustment to independent living (for those away from home for the first time, even if just from rural County Armagh). Student mental health services are often overwhelmed with long waiting lists, leaving many struggling without support.
                  </p>
                  <p>
                    Hypnotherapy provides targeted help for students: exam preparation and performance anxiety reduction (managing the "blank mind" panic during tests), study motivation and focus enhancement, sleep optimization for irregular student schedules, confidence building for presentations and group work, managing imposter syndrome ("I don't belong here"), and stress management during assignment deadlines. Online sessions fit perfectly around your college timetable - no need to travel to Belfast between lectures.
                  </p>
                  <p>
                    Many students report that hypnotherapy helps them access their actual knowledge during exams (reducing the gap between what they know and what they can recall under pressure), improves concentration during study sessions, and provides coping strategies for the specific pressures of college life. The investment in a 4-session package can transform your entire academic experience - and online delivery means you can fit sessions around lectures, labs, and library time without adding travel stress.
                  </p>
                </div>
              </div>

              {/* Religious Community Stress */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Religious Community Stress and Identity Pressure
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Armagh's identity as the ecclesiastical capital of Ireland - home to both St Patrick's Cathedral (Catholic) and the Church of Ireland Cathedral - creates a unique context where religious identity remains more visible and central than in many other Northern Irish communities. For some, this provides comfort, tradition, and strong social networks. For others, it creates pressure: expectations to maintain religious observance when personal beliefs have changed, anxiety about community judgment, stress from religious obligations that feel burdensome rather than meaningful, and the challenge of navigating personal identity within traditional frameworks.
                  </p>
                  <p>
                    I want to be clear: hypnotherapy isn't about changing your beliefs or persuading you away from faith. It's about helping you navigate the <em>stress</em> that can arise from religious community dynamics - the gap between external expectations and internal experience. This might include: anxiety about attending services when you're questioning faith (without wanting to disappoint family), stress from church community conflicts or politics, pressure to conform to traditional gender roles or life paths, guilt about not being "devout enough," or the challenge of establishing boundaries with religious family or community members.
                  </p>
                  <p>
                    Online sessions are particularly valuable here because they provide complete confidentiality - no one in Armagh's close-knit community needs to know you're seeking support. You can explore these sensitive topics in a professional, non-judgmental space without the visibility of attending a physical clinic. Hypnotherapy helps you clarify your own values, reduce anxiety around community expectations, and establish authentic boundaries while maintaining the relationships that matter to you.
                  </p>
                </div>
              </div>

              {/* Rural Isolation and Limited Services */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Rural Isolation and Limited Mental Health Services
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    While Armagh city itself is the urban center, much of County Armagh is rural - farms, small villages, scattered homes surrounded by orchard country and rolling hills. This rural character brings beauty and community connection, but also challenges: geographic isolation (nearest neighbors may be distant), limited public transport, fewer entertainment and social opportunities than cities, and significantly reduced access to mental health services. The nearest specialized therapy is often in Belfast - a 40+ mile journey that's simply not practical for regular appointments.
                  </p>
                  <p>
                    Rural isolation contributes to specific mental health patterns: loneliness and social disconnection (especially for those living alone or working from home), anxiety about medical emergencies when hospitals are distant, stress from being "stuck" without easy access to amenities, seasonal affective disorder worsened by long winter evenings in isolated locations, and the sense of being "left behind" as young people migrate to cities for opportunities.
                  </p>
                  <p>
                    Online hypnotherapy breaks through these barriers. You don't need to be near Belfast or have access to regular transport - if you have internet connection, you have access to professional therapy. Sessions address rural-specific concerns: managing isolation through building internal resilience, reducing anxiety about distance from services, finding meaning and connection in rural life rather than feeling trapped, and developing coping strategies for the unique pressures of agricultural or small-community living. You're not forgotten just because you're not in the city.
                  </p>
                </div>
              </div>

              {/* General Anxiety and Stress */}
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  General Anxiety and Stress Management
                </h3>
                <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                  <p>
                    Beyond Armagh-specific stressors, many residents experience generalized anxiety - persistent worry, racing thoughts, physical tension, sleep disturbances, panic attacks, and the overwhelming feeling that something bad is always about to happen. This might be triggered by work pressures, family dynamics, financial stress, health concerns, or nothing obvious at all - just a constant background hum of unease that won't switch off.
                  </p>
                  <p>
                    Hypnotherapy addresses anxiety at the subconscious level where automatic worry patterns are stored. Instead of just managing symptoms with conscious coping strategies (helpful but incomplete), clinical hypnosis helps retrain the nervous system's threat response. Techniques include: progressive relaxation to release chronic physical tension, cognitive reframing to challenge catastrophic thinking patterns, visualization to create a felt sense of safety, anchoring techniques to access calm states during anxious moments, and subconscious suggestion to reduce the baseline anxiety level that colors your entire day.
                  </p>
                  <p>
                    Research evidence supports hypnotherapy for anxiety disorders, with some studies finding it as effective as cognitive behavioral therapy (CBT). Many clients report significant improvement within 4-6 sessions - not that anxiety disappears entirely, but that it becomes manageable rather than overwhelming. Online delivery means you can receive this evidence-based treatment from the comfort of your own Armagh home, without the additional stress of traveling to Belfast while already anxious.
                  </p>
                </div>
              </div>

              {/* Additional Services */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Sleep Problems and Insomnia
                  </h4>
                  <p className="text-gray-700">
                    Racing thoughts keeping you awake? Early morning anxiety waking you at 4am? Hypnotherapy helps quiet mental chatter and retrain your nervous system for restful sleep - essential when you're already dealing with stress, study pressures, or rural isolation.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Smoking Cessation and Habit Change
                  </h4>
                  <p className="text-gray-700">
                    Ready to quit smoking? Break stress-eating patterns? Address nail-biting or other unwanted habits? Hypnotherapy targets the subconscious drivers of automatic behaviors - more effective than willpower alone, and delivered online for Armagh convenience.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Chronic Pain Management
                  </h4>
                  <p className="text-gray-700">
                    Living with fibromyalgia, back pain, migraines, or other persistent pain? Hypnotherapy helps reduce pain intensity and improve quality of life by working with your nervous system's pain processing. NHS-complementary support, accessible online from Armagh.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                    Confidence and Self-Esteem
                  </h4>
                  <p className="text-gray-700">
                    Struggling with imposter syndrome at college or work? Social anxiety in Armagh's close-knit community? Hypnotherapy builds genuine confidence from within - authentic self-acceptance rather than fake-it-till-you-make-it pretense.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There / Online Setup Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Accessing Therapy from Armagh
            </h2>

            <div className="bg-white rounded-xl p-8 mb-8 border-2 border-accent-500">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <Video className="w-6 h-6 text-accent-600 mr-3" />
                Online Sessions (Recommended for Armagh)
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="font-semibold text-lg">Same quality therapy, zero commute - the smart choice for 40+ mile distance</p>
                <p>
                  <strong>How it works:</strong> Sessions conducted via secure video platform (Zoom or similar). You need a private space, reliable internet connection, and a device with camera/microphone (phone, tablet, laptop, or desktop computer all work).
                </p>
                <p><strong>Effectiveness:</strong> Research confirms online hypnotherapy is equally effective as in-person. Hypnosis works through verbal guidance and your subconscious processes - physical proximity isn't necessary. You receive the same professional CNHC-registered therapy, just delivered conveniently to your Armagh home.</p>
                <p><strong>Privacy:</strong> Completely confidential. No one in Armagh's close-knit community needs to know you're seeking support. Sessions appear as regular video calls - no identifying information visible to others in your household if privacy is a concern.</p>
                <p><strong>Scheduling:</strong> Flexible appointment times including evenings and weekends. Perfect for Southern Regional College students (fit around lectures), working professionals (lunchtime or after work), parents (during school hours), or anyone with limited availability.</p>
                <p className="text-sm italic">Online sessions save you 2+ hours of travel time per appointment, eliminate fuel costs, and make consistent weekly therapy actually sustainable for busy Armagh schedules.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4 flex items-center">
                <Car className="w-6 h-6 text-accent-600 mr-3" />
                Belfast Clinic Option (If You Prefer In-Person)
              </h3>
              <div className="space-y-3 text-gray-700">
                <p className="font-semibold">Distance: 40+ miles via M1/A3 | Drive time: 50-60 minutes each way</p>
                <p>
                  <strong>To be clear:</strong> While our Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast is available for Armagh clients who prefer face-to-face sessions, the distance is significant. The 40-mile journey takes 50-60 minutes, meaning each appointment requires over 2 hours of travel time plus the session itself. For regular weekly therapy, this quickly becomes unsustainable for most people.
                </p>
                <p><strong>Route (if you choose in-person):</strong></p>
                <ol className="list-decimal list-inside space-y-1 ml-4">
                  <li>From Armagh city center, head northeast on A3 toward Portadown</li>
                  <li>Join M1 motorway at Portadown toward Belfast</li>
                  <li>Continue on M1 for approximately 30 miles into Belfast</li>
                  <li>Leave the motorway for south Belfast and join the Ormeau Road (A24)</li>
                  <li>The clinic is at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ, about 10 minutes from the city centre</li>
                </ol>
                <p><strong>Hybrid approach:</strong> Some clients choose initial consultation in-person (to meet face-to-face and ensure comfortable rapport) then switch to online for subsequent sessions. This provides connection while maintaining sustainability. Discuss your preferences during booking.</p>
                <p className="text-sm italic font-semibold">Honest recommendation: For most Armagh clients, online sessions provide better long-term outcomes simply because they're easier to maintain consistently. Missing appointments due to travel challenges interrupts therapeutic progress.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Questions from Armagh Clients
            </h2>
            <div className="space-y-6">

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do I need to travel to Belfast for hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  No - online sessions are recommended for Armagh clients due to the 40+ mile distance. Online hypnotherapy is equally effective as in-person (research-backed), and you receive the same professional CNHC-registered therapy without losing 2+ hours to travel. Belfast clinic is available if you have a strong preference for face-to-face, but most Armagh clients find online more sustainable for regular appointments.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are online sessions as effective as in-person hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  Yes. Clinical research shows no significant difference in outcomes between online and in-person hypnotherapy. Hypnosis works through verbal guidance, visualization, and accessing your subconscious mind - none of which require physical proximity. The therapeutic relationship is built through voice, trust, and your engagement with the process. Many clients actually prefer online because they're more relaxed in their own familiar space.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Do you work with students from Southern Regional College?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Student mental health is a specialty area - I understand the unique pressures of college life (exams, assignments, social adjustment, career uncertainty, financial stress). Online sessions fit perfectly around your timetable - no need to miss lectures or travel to Belfast. Many students see significant improvement in exam performance, study focus, and general wellbeing within 4-6 sessions. Student-friendly pricing available (discuss during consultation).
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Can you help with religious community stress without being judgmental?
                </h3>
                <p className="text-gray-700">
                  Yes - and complete confidentiality is guaranteed. Hypnotherapy addresses the stress and anxiety arising from community expectations, not your beliefs themselves. Whether you're questioning faith, feeling pressured by religious obligations, navigating family expectations, or experiencing guilt around changing beliefs, this is a non-judgmental space. Online sessions ensure complete privacy in Armagh's close-knit community - no one needs to know you're seeking support.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy suitable for rural isolation and limited social connection?
                </h3>
                <p className="text-gray-700">
                  Yes. Rural isolation in County Armagh creates specific challenges - loneliness, limited mental health services locally, feeling "stuck" or left behind. Hypnotherapy helps build internal resilience, manage isolation anxiety, and develop coping strategies for rural living. Online delivery means you access professional support regardless of your distance from Armagh city or public transport limitations - if you have internet, you have access to therapy.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What do I need for online sessions?
                </h3>
                <p className="text-gray-700">
                  You need: (1) A private space where you won't be interrupted (bedroom, home office, even parked car works), (2) Reliable internet connection for video call, (3) Device with camera and microphone (smartphone, tablet, laptop, or desktop all work), (4) Headphones/earbuds recommended for better audio quality and privacy. Sessions conducted via secure video platform (Zoom or similar) - simple to use even if you're not tech-savvy.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  This varies by individual and concern. Many clients notice improvement within 4-6 sessions. Some issues (specific phobias, smoking cessation, exam anxiety) may respond in 1-3 sessions. Deeper patterns (chronic anxiety, long-term stress, complex religious community issues) may benefit from 8-12 sessions. We'll discuss realistic expectations during your free initial consultation and create a plan that fits your needs and budget.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Is hypnotherapy covered by health insurance?
                </h3>
                <p className="text-gray-700">
                  Some private health insurance plans cover hypnotherapy if provided by a CNHC-registered practitioner (I am). Check your policy or contact your insurer. If not covered, package discounts are available when you book multiple sessions. Many students and working adults find the investment worthwhile compared to ongoing costs of stress, poor academic performance, or mental health struggles.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Are you qualified? How do I verify CNHC registration?
                </h3>
                <p className="text-gray-700">
                  Yes - I'm registered with the CNHC (Complementary and Natural Healthcare Council), the UK regulatory body for hypnotherapists accredited by the Professional Standards Authority. This requires 450+ hours of training, professional membership, insurance, and adherence to strict ethical standards. You can verify my registration at www.cnhc.org.uk by searching for my name. I also have 15 years of nursing experience at Belfast City Hospital, bringing healthcare professionalism to therapy.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  What happens in an online hypnotherapy session?
                </h3>
                <p className="text-gray-700">
                  Sessions begin with discussion about your concerns and goals via video call (what brings you, what you want to change). Then guided relaxation - I help you into a calm, focused state through verbal guidance (you remain fully aware and in control, just deeply relaxed). In this state, we work with your subconscious mind using visualization, suggestion, and reframing techniques specific to your issue. You emerge feeling calm and refreshed. Each session tailored to your needs, not a generic script.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              What Clients from County Armagh Say
            </h2>
            <div className="grid md:grid-cols-2 gap-8">

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "As a student at Southern Regional College, the exam anxiety was overwhelming. Sharon's online sessions fit perfectly around my timetable - I could book appointments between lectures without the stress of traveling to Belfast. After 5 sessions, my exam performance improved dramatically. I can actually access what I've studied instead of blanking under pressure. Highly recommend for any students struggling."
                </p>
                <p className="text-gray-900 font-semibold">— M.K., Armagh City</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Living in rural County Armagh, accessing mental health support felt impossible - the nearest services were in Belfast, 40 miles away. Online hypnotherapy was a revelation. Sharon helped me manage the isolation anxiety and find meaning in rural life rather than feeling trapped. The confidentiality was important too - no one in our small community needs to know I'm getting help. Can't recommend enough."
                </p>
                <p className="text-gray-900 font-semibold">— P.O., Callan Bridge</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The stress from church community expectations was affecting my sleep and relationships, but I couldn't talk about it locally - everyone knows everyone in Armagh. Sharon provided a completely non-judgmental space to work through the anxiety. Online sessions meant complete privacy, and the techniques she taught me have been transformative. I can navigate community obligations with much less stress now."
                </p>
                <p className="text-gray-900 font-semibold">— S.B., Drumbreda</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I quit smoking after 20 years with Sharon's help - only needed 3 online sessions. The hypnotherapy approach worked where everything else had failed. Being able to do sessions from home made it so much easier to commit to regular appointments. The money I'm saving on cigarettes has already paid for the therapy many times over. Armagh residents - don't let the distance put you off, online is just as effective."
                </p>
                <p className="text-gray-900 font-semibold">— D.M., Tullygally</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-start space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-accent-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The generalized anxiety I'd struggled with for years has reduced significantly after working with Sharon online. I was skeptical about video therapy at first, but it's genuinely just as good as face-to-face - maybe better because I'm in my own space and don't have the added stress of the Belfast drive. Sharon understands the specific pressures of life in Armagh and County Armagh. Professional, warm, and effective."
                </p>
                <p className="text-gray-900 font-semibold">— L.H., Armagh City</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />

    </main>
  );
};

export default ArmaghHypnotherapy;
